import { Helmet } from "react-helmet-async";
import {
  SITE_NAME,
  SITE_URL,
  SITE_DESCRIPTION,
  DEFAULT_OG_IMAGE,
  absoluteUrl,
} from "@/lib/seo-config";

/**
 * <Seo /> — drop into any page to set SEO meta tags.
 *
 * Props:
 *   title         Page-level title (will be suffixed with " · Flycher")
 *   description   Meta description (falls back to default)
 *   path          Canonical path (e.g. "/explore/vietnam")
 *   image         Absolute OG image URL (falls back to brand default)
 *   type          OG type — defaults to "website"
 *   noindex       If true, sets robots noindex,nofollow
 *   jsonLd        Optional object (or array of objects) for structured data
 */
export function Seo({
  title,
  description,
  path = "/",
  image,
  type = "website",
  noindex = false,
  jsonLd,
}) {
  const fullTitle = title
    ? `${title} · ${SITE_NAME}`
    : `${SITE_NAME} — ${SITE_DESCRIPTION.split(" — ")[0]}`;
  const desc = description || SITE_DESCRIPTION;
  const canonical = absoluteUrl(path);
  const ogImage = image || DEFAULT_OG_IMAGE;

  const ldArray = Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : [];

  return (
    <Helmet prioritizeSeoTags>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      <link rel="canonical" href={canonical} />
      <meta
        name="robots"
        content={noindex ? "noindex,nofollow" : "index,follow,max-image-preview:large,max-snippet:-1"}
      />

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:locale" content="en_IN" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={ogImage} />

      {/* Region targeting — Flycher is built for India */}
      <meta name="geo.region" content="IN" />

      {/* Structured data */}
      {ldArray.map((ld, i) => (
        <script
          key={i}
          type="application/ld+json"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: JSON.stringify(ld) }}
        />
      ))}
    </Helmet>
  );
}

// --- Convenience structured-data builders -----------------------------------

export const organizationLd = () => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  name: SITE_NAME,
  url: SITE_URL,
  logo: `${SITE_URL}/logo.png`,
  sameAs: [],
  description: SITE_DESCRIPTION,
});

export const websiteLd = () => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: SITE_NAME,
  url: SITE_URL,
  potentialAction: {
    "@type": "SearchAction",
    target: `${SITE_URL}/explore?q={search_term_string}`,
    "query-input": "required name=search_term_string",
  },
});

export const breadcrumbLd = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((it, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: it.name,
    item: absoluteUrl(it.path),
  })),
});

export const touristDestinationLd = ({ name, description, image, path }) => ({
  "@context": "https://schema.org",
  "@type": "TouristDestination",
  name,
  description,
  url: absoluteUrl(path),
  image,
});

// ----- Rich-snippet-worthy schemas (Google's recent helpful-content + AIO favor these) ---

/**
 * FAQPage — eligible for FAQ rich result in SERP.
 * Pass an array of { question, answer } objects.
 */
export const faqLd = (items = []) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: items.filter((i) => i?.question && i?.answer).map((i) => ({
    "@type": "Question",
    name: i.question,
    acceptedAnswer: { "@type": "Answer", text: i.answer },
  })),
});

/**
 * HowTo — eligible for HowTo rich result.
 * steps: array of { name, text }
 */
export const howToLd = ({ name, description, totalTime, image, steps = [] }) => ({
  "@context": "https://schema.org",
  "@type": "HowTo",
  name,
  description,
  ...(totalTime ? { totalTime } : {}),
  ...(image ? { image } : {}),
  step: steps.map((s, i) => ({
    "@type": "HowToStep",
    position: i + 1,
    name: s.name,
    text: s.text || s.name,
  })),
});

/**
 * Article / BlogPosting — eligible for Article rich result + Google Discover.
 */
export const articleLd = ({
  headline,
  description,
  image,
  path,
  datePublished,
  dateModified,
  authorName = "Flycher Editorial",
}) => ({
  "@context": "https://schema.org",
  "@type": "Article",
  headline,
  description,
  image,
  mainEntityOfPage: absoluteUrl(path),
  author: { "@type": "Organization", name: authorName },
  publisher: {
    "@type": "Organization",
    name: SITE_NAME,
    logo: { "@type": "ImageObject", url: `${SITE_URL}/logo.png` },
  },
  datePublished: datePublished || new Date().toISOString().split("T")[0],
  dateModified: dateModified || new Date().toISOString().split("T")[0],
});

/**
 * SoftwareApplication / WebApplication — for our tool pages.
 */
export const webAppLd = ({ name, description, path, category = "TravelApplication" }) => ({
  "@context": "https://schema.org",
  "@type": "WebApplication",
  name,
  description,
  url: absoluteUrl(path),
  applicationCategory: category,
  operatingSystem: "Any",
  offers: { "@type": "Offer", price: "0", priceCurrency: "INR" },
  inLanguage: "en-IN",
});

/**
 * Product schema — for forex cards comparison page (rich snippet eligible).
 */
export const productLd = ({ name, description, brand, rating, reviewCount = 50 }) => ({
  "@context": "https://schema.org",
  "@type": "Product",
  name,
  description,
  brand: { "@type": "Brand", name: brand },
  ...(rating ? {
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: rating,
      reviewCount,
      bestRating: 5,
      worstRating: 1,
    },
  } : {}),
});
